import { useContext, useEffect, useState } from "react";
import {
    Box, Card, Chip, CircularProgress, IconButton, Option, Select, Stack,
    Tab, TabList, TabPanel, Tabs, Typography, Alert,
} from "@mui/joy";

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import ReportRoundedIcon from '@mui/icons-material/ReportRounded';
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

import { usePartner } from "../../PartnerContext";
import {
    APIGetPartnerTips,
    APIGetPartnerRecommendations,
    APIDismissPartnerRecommendation,
} from "../../api_partner";


const CATEGORIES = [
    { value: '', label: 'Все категории' },
    { value: 'onboarding', label: 'Начало работы' },
    { value: 'growth', label: 'Рост' },
    { value: 'finance', label: 'Финансы' },
    { value: 'compliance', label: 'Правила' },
    { value: 'other', label: 'Другое' },
];

const SEVERITY_ICON = {
    info: <InfoRoundedIcon />,
    warning: <WarningRoundedIcon />,
    critical: <ReportRoundedIcon />,
};

const SEVERITY_COLOR = {
    info: 'primary',
    warning: 'warning',
    critical: 'danger',
};


export default function PartnerTips() {
    const { partnerToken: token } = usePartner();
    const [tab, setTab] = useState(0);

    const [tips, setTips] = useState([]);
    const [tipsCategory, setTipsCategory] = useState('');
    const [tipsLoading, setTipsLoading] = useState(true);

    const [recs, setRecs] = useState([]);
    const [recsLoading, setRecsLoading] = useState(true);

    const loadTips = async (cat = tipsCategory) => {
        try {
            setTipsLoading(true);
            const res = await APIGetPartnerTips(token, cat || null);
            setTips(res?.results || []);
        } catch (e) { console.error(e); }
        finally { setTipsLoading(false); }
    };

    const loadRecs = async () => {
        try {
            setRecsLoading(true);
            const res = await APIGetPartnerRecommendations(token);
            setRecs(res?.results || []);
        } catch (e) { console.error(e); }
        finally { setRecsLoading(false); }
    };

    useEffect(() => { if (token) { loadTips(); loadRecs(); } }, [token]);

    const dismiss = async (id) => {
        try {
            await APIDismissPartnerRecommendation(token, id);
            setRecs(prev => prev.filter(r => r.id !== id));
        } catch (e) { console.error(e); }
    };

    return (
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <TabList>
                <Tab>
                    <LightbulbRoundedIcon sx={{ mr: 0.5 }} fontSize="small" />
                    Советы
                </Tab>
                <Tab>
                    Рекомендации
                    {recs.length > 0 && (
                        <Chip size="sm" color="danger" variant="solid" sx={{ ml: 0.7 }}>
                            {recs.length}
                        </Chip>
                    )}
                </Tab>
            </TabList>

            {/* Советы */}
            <TabPanel value={0} sx={{ p: 0, pt: 2 }}>
                <Stack spacing={2}>
                    <Select
                        size="sm"
                        value={tipsCategory}
                        onChange={(_, v) => { setTipsCategory(v); loadTips(v); }}
                        sx={{ maxWidth: 240 }}
                    >
                        {CATEGORIES.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                    </Select>

                    {tipsLoading ? (
                        <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>
                    ) : tips.length === 0 ? (
                        <Typography level="body-sm" color="neutral">Советов пока нет</Typography>
                    ) : (
                        <Stack spacing={1.5}>
                            {tips.map(t => (
                                <Card key={t.id} variant="outlined" sx={{ p: 2 }}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                        <Typography level="title-md">{t.title}</Typography>
                                        <Chip size="sm" variant="soft">
                                            {CATEGORIES.find(c => c.value === t.category)?.label || t.category}
                                        </Chip>
                                    </Stack>
                                    <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {t.body}
                                    </Typography>
                                </Card>
                            ))}
                        </Stack>
                    )}
                </Stack>
            </TabPanel>

            {/* Рекомендации */}
            <TabPanel value={1} sx={{ p: 0, pt: 2 }}>
                {recsLoading ? (
                    <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>
                ) : recs.length === 0 ? (
                    <Card variant="soft" color="success" sx={{ p: 3, textAlign: 'center' }}>
                        <Typography level="title-md" startDecorator={<CheckCircleRoundedIcon />}>
                            Всё в порядке
                        </Typography>
                        <Typography level="body-sm" color="neutral">
                            Активных рекомендаций нет — продолжайте в том же духе.
                        </Typography>
                    </Card>
                ) : (
                    <Stack spacing={1.5}>
                        {recs.map(r => (
                            <Alert
                                key={r.id}
                                color={SEVERITY_COLOR[r.severity] || 'primary'}
                                variant="soft"
                                startDecorator={SEVERITY_ICON[r.severity]}
                                endDecorator={
                                    <IconButton
                                        size="sm"
                                        variant="plain"
                                        color={SEVERITY_COLOR[r.severity] || 'primary'}
                                        onClick={() => dismiss(r.id)}
                                    >
                                        <CloseRoundedIcon />
                                    </IconButton>
                                }
                            >
                                <Box>
                                    <Typography level="title-sm">{r.title}</Typography>
                                    <Typography level="body-sm">{r.body}</Typography>
                                </Box>
                            </Alert>
                        ))}
                    </Stack>
                )}
            </TabPanel>
        </Tabs>
    );
}
